// pages/shop/ProductListView.jsx (CRA)
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';

import Pagination from '../../components/nav/Pagination'; // 🔁 adjust path
import ProductSideBar from '../../components/products/ProductSideBar';
import Empty from '../../components/feedback/Empty';
import ProductCard from '../../components/products/ProductCard';
import { useSession } from '../../context/SessionContext';
import productServices from '../../services/ProductsServices';
import { Helmet } from 'react-helmet';
import utils from '../../utils';
import Search from '../../components/nav/Search';

function ProductListView() {
  const { productTypes = [], } = useSession();
  const [searchParams,] = useSearchParams()
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState(null);

  const parseParamArray = (paramKey) => {
    return searchParams.getAll(paramKey)
      .map(id => parseInt(id, 10))
      .filter(id => !isNaN(id));
  };

  const selectedCategories = parseParamArray('product_type');
  const selectedBrands = parseParamArray('brand');
  const selectedModels = parseParamArray('model');
  const currentOrdering = searchParams.get('ordering') || '';

  // Dynamic page title
  let pageTitle = 'Products';

  if (selectedCategories.length === 1) {
    const category = productTypes.find(cat => cat.id === selectedCategories[0]);
    if (category) pageTitle = category.name;
  }

  // Fetch when URL params change
  useEffect(() => {
    const paramsStr = searchParams.toString();
    const fetchProducts = async () => {
      setError(null);
      setLoading(true);
      try {
        const res = await productServices.getProducts(paramsStr ? `?${paramsStr}` : '');
        setProducts(res.data);
      } catch (err) {
        console.error('Product fetch error:', err);
        setError(err?.response?.data?.detail || 'Failed to load products');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [searchParams]);

  const updateURL = (newParams, scroll = false) => {
    const newPath = {
      pathname: location.pathname,
      search: newParams.toString(),
    };
    navigate(newPath, { replace: true, state: { scroll } });
  };

  const handleFilterChange = (paramKey, newValues) => {
    const params = new URLSearchParams(searchParams);

    if (paramKey === null && newValues?.clear) {
      params.delete('product_type');
      params.delete('brand');
      params.delete('model');
      params.delete('search');
      params.delete('is_featured');
      params.delete('ordering');
    } else if (paramKey) {
      params.delete(paramKey);
      newValues.forEach(value => {
        if (value !== undefined && value !== null) {
          params.append(paramKey, String(value));
        }
      });
    }

    params.delete('page');
    updateURL(params);
  };

  const handleOrderingChange = (e) => {
    const value = e.target.value;
    const params = new URLSearchParams(searchParams);

    if (value) {
      params.set('ordering', value);
    } else {
      params.delete('ordering');
    }
    params.delete('page');

    updateURL(params);
  };

  const changePageParam = (page) => {
    const params = new URLSearchParams(searchParams);
    if (page && page !== 1) {
      params.set('page', String(page));
    } else {
      params.delete('page');
    }
    window.scrollTo(0, 0);
    updateURL(params);
  };

  return (
    <div>
      <Helmet>
        <title>{pageTitle} - {utils.siteName}</title>
      </Helmet>
      <div className="container mx-auto px-6 md:px-10 lg:px-20 py-4">
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="w-full lg:w-1/4 lg:max-w-xs flex-shrink-0 gap-3">
            <ProductSideBar
              selectedCategories={selectedCategories}
              selectedBrands={selectedBrands}
              selectedModels={selectedModels}
              onFilterChange={handleFilterChange}
            />
          </div>
          <div className="w-full lg:w-3/4">
            <div className="md:hidden mb-4 sticky top-[64px] bg-white z-10">
              <Search />
            </div>
            <div className="">
              <div className="flex flex-col sm:flex-row justify-between mb-6">
                <div>
                  <h1 className="text-3xl text-primary font-bold">{pageTitle}</h1>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 sm:mb-0">
                    Showing {products?.results?.length || 0} of {products?.count || 0} products
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4 sm:mb-0">
                  <p className="whitespace-nowrap">Sort By:</p>
                  <select
                    className="select w-full sm:w-auto border"
                    value={currentOrdering}
                    onChange={handleOrderingChange}
                  >
                    <option value="">Default</option>
                    <option value="-creation_time">Newest</option>
                    <option value="creation_time">Oldest</option>
                    <option value="price">Price: Low to High</option>
                    <option value="-price">Price: High to Low</option>
                    <option value="name">Name: A to Z</option>
                    <option value="-name">Name: Z to A</option>
                  </select>
                </div>
              </div>
            </div>

            <div>
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="group relative flex flex-col overflow-hidden rounded-lg border border-border-light dark:border-border-dark bg-white dark:bg-background-dark animate-pulse">
                      <div className="aspect-square overflow-hidden bg-gray-200 dark:bg-gray-700">
                        <div className="h-full w-full"></div>
                      </div>
                      <div className="flex flex-1 flex-col p-4">
                        <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>

                        <div className="mt-auto h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
                      </div>
                      <div className="p-3">
                        <div className="w-full h-10 bg-gray-200 dark:bg-gray-700 rounded-md"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : error ? (
                <section className="py-10">
                  <div className="container mx-auto px-4 text-center">
                    <p className="text-red-600">{error}</p>
                  </div>
                </section>
              ) : products?.results?.length ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-3 gap-6">
                  {products.results.map((product) => (
                    <ProductCard product={product} key={product.id || product.slug} />
                  ))}
                </div>
              ) : (
                <Empty objectName="Products" />
              )}
            </div>

            {!loading && !error && products && products.count > 0 && (
              <div className="flex justify-center mt-12">
                <Pagination
                  count={products.count}
                  next={products.next}
                  previous={products.previous}
                  changePageParam={changePageParam}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductListView;